// Canonical display of monetary amounts.
//
// Every amount in the database and in the pricing engine is an integer number
// of øre — never a float of kroner — so the conversion to a human string is
// the same operation everywhere and belongs in one place.
//
// Two rounding modes, and the distinction is deliberate:
//
//   formatKr       whole kroner — prices, fees, estimates. The pricing
//                  engine rounds bands to the nearest 50 kr, so decimals are
//                  noise the customer should not have to read.
//   formatExactKr  up to two decimals — the ledger only. A partner balance is
//                  an exact sum of charges and top-ups; hiding the øre
//                  remainder there would make the column not add up.

const WHOLE_KR = new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 });
const EXACT_KR = new Intl.NumberFormat("da-DK", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * "2.400" — the number alone, no unit. For input fields that carry their own
 * "kr" affix, and for callers composing a longer string.
 */
export function oereToKrString(oere: number): string {
  return WHOLE_KR.format(Math.round(oere / 100));
}

/** "2.400 kr." — the default for any price shown to a customer or partner. */
export function formatKr(oere: number): string {
  return `${oereToKrString(oere)} kr.`;
}

/**
 * "2.000–2.400 kr." — a price span with the unit stated once. Collapses to a
 * single amount when the bounds are equal.
 */
export function formatKrRange(minOere: number, maxOere: number): string {
  if (minOere === maxOere) return formatKr(minOere);
  return `${oereToKrString(minOere)}–${formatKr(maxOere)}`;
}

/** "2.400,50 kr." — ledger amounts, where the øre remainder is real money. */
export function formatExactKr(oere: number): string {
  return `${EXACT_KR.format(oere / 100)} kr.`;
}

/**
 * "+2.400,50 kr." / "−2.400,50 kr." — a signed ledger movement. Uses U+2212
 * MINUS SIGN rather than a hyphen so the column aligns with the plus.
 */
export function formatSignedKr(oere: number): string {
  const sign = oere >= 0 ? "+" : "−";
  return `${sign}${formatExactKr(Math.abs(oere))}`;
}
